import React, { useState, useCallback } from 'react';
import IconButton from '@mui/material/IconButton';
import { withStyles } from '@mui/styles';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import { enqueueSnackbar } from 'notistack';
import CircularProgress from '@mui/material/CircularProgress';
import { getErrorMessageFromResponse } from '../../common/helper';
import Button from '@mui/material/Button/Button';

const styles = theme => ({});

const DownloadResults = (props) => {
    const [isFetching, setIsFetching] = useState(false);
    const debug = window.location.pathname.toLowerCase().includes('debug')

    const download = useCallback(async () => {
        setIsFetching(true);

        const pathVar = '/download';
        const url =
            process.env.REACT_APP_CONTENT_API_BASE_URL +
            process.env.REACT_APP_CONTENT_API_DOCUMENT +
            pathVar;

        // Build array of ids from the search results
        const files = props.searchResults.map(sr => sr.id);

        const request = {
            method: 'POST',
            headers: {"Authorization": "Bearer " + props.userDetails.accessToken},
            body: files
        };

        debug && console.log('url = ', url, 'request = ', request);

        try {
            await props.triggerRefreshAuthToken();

            const response = await fetch(url, request);
            debug && console.log('response=', response);

            if (!response.ok) {
                const message = await getErrorMessageFromResponse(
                    response,
                    'downloading files'
                );

                const options = { variant: 'error' };
                if (response.status === 401 && props.logout) {
                    options.action = (
                        <Button
                            style={{ color: 'white' }}
                            onClick={() => props.logout()}
                        >
                            Logout
                        </Button>
                    );
                    options.autoHideDuration = 10000;
                }

                enqueueSnackbar(message, options);
                setIsFetching(false);
                return;
            }

            const result = await response.blob();

            if (result) {
                const blobUrl = URL.createObjectURL(result);
                const fileName =
                    files.length === 1
                        ? props.searchResults[0].name
                        : props.fileName;

                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = fileName || 'download';
                a.click();

                enqueueSnackbar('Download complete', { variant: 'success' });
            }
        } catch (e) {
            debug && console.log('download Exception:', e, ' url:', url, 'request:', request);

            enqueueSnackbar('Error downloading documents: ' + e, {variant: 'error'});
        } finally {
            setIsFetching(false);
        }
    }, [props, debug]);

    const progressMessage =
        props.searchResults.length > 1
            ? `Downloading ${props.searchResults.length} files as zip file`
            : `Downloading ${props.searchResults[0].name}`;

    const tooltipTitle =
        props.searchResults.length === 1
            ? `${props.downloadResultsActionConfig.label} 1 file`
            : `${props.downloadResultsActionConfig.label} all ${props.searchResults.length} files`;

    return (
        <div style={{ display: 'inline' }}>
            <Tooltip title={isFetching ? progressMessage : tooltipTitle}>
                <span>
                    <IconButton
                        aria-label="Menu"
                        aria-haspopup="true"
                        color="secondary"
                        onClick={download}
                        disabled={isFetching}
                        size="medium"
                    >
                        {isFetching ? (
                            <CircularProgress color="secondary" size={20} />
                        ) : (
                            <i className="material-icons md-36">file_download</i>
                        )}
                    </IconButton>
                </span>
            </Tooltip>
        </div>
    );
};

DownloadResults.propTypes = {
    userDetails: PropTypes.object.isRequired,
    downloadResultsActionConfig: PropTypes.object.isRequired,
    searchResults: PropTypes.array.isRequired,
    fileName: PropTypes.string.isRequired,
    triggerRefreshAuthToken: PropTypes.func.isRequired,
    logout: PropTypes.func
};

export default withStyles(styles)(DownloadResults);